import React from 'react'
import classNames from 'classnames'
import PropTypes from 'prop-types'
import ReactSelect from 'react-select'
import tw, { theme } from 'twin.macro'
import isEmpty from 'lodash/isEmpty'
import get from 'lodash/get'
import { useConfig } from '../ConfigProvider'
import { useForm } from '../Form/context'
import { useInputGroup } from '../InputGroup/context'
import { HiCheck, HiX } from 'react-icons/hi'
import Spinner from '../Spinner'
import { SIZES } from '../utils/constant'

const DefaultOption = ({ innerProps, label, selectProps, isSelected, isDisabled }) => {
    const { themeColor } = selectProps
    return (
        <div
            className={`select-option ${isSelected && 'selected'} ${isDisabled && 'disabled'}`}
            {...innerProps}
        >
            <span className="ml-2">{label}</span>
            {isSelected && <HiCheck className={`text-${themeColor} dark:text-white text-xl`} />}
        </div>
    )
}

// ── Custom dropdown indicator — stacked up/down arrows ───────────────────────
const DefaultDropdownIndicator = () => {
    return (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            paddingRight: '8px',
            paddingLeft: '2px',
        }}>
            <svg
                width="12"
                height="14"
                viewBox="0 0 12 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                {/* Up arrow */}
                <path
                    d="M6 1.5L9.5 5.5H2.5L6 1.5Z"
                    fill="#374151"
                />
                {/* Down arrow */}
                <path
                    d="M6 12.5L2.5 8.5H9.5L6 12.5Z"
                    fill="#374151"
                />
            </svg>
        </div>
    )
}

const DefaultClearIndicator = props => {
    const { innerProps: { ref, ...restInnerProps } } = props
    return (
        <div {...restInnerProps} ref={ref}>
            <div className="select-clear-indicator">
                <HiX />
            </div>
        </div>
    )
}

const DefaultLoadingIndicator = ({ selectProps }) => {
    const { themeColor } = selectProps
    return (
        <Spinner className={`select-loading-indicatior text-${themeColor}`} />
    )
}

const Select = React.forwardRef((props, ref) => {
    const {
        size,
        style,
        className,
        form,
        field,
        components,
        componentAs: Component,
        ...rest
    } = props

    const { themeColor, controlSize, primaryColorLevel, mode } = useConfig()
    const formControlSize = useForm()?.size
    const inputGroupSize = useInputGroup()?.size

    const selectSize = size || inputGroupSize || formControlSize || controlSize

    const twColor = theme`colors`
    const twHeight = theme`height`

    let isInvalid = false
    if (!isEmpty(form)) {
        const { touched, errors } = form
        const touchedField = get(touched, field.name)
        const errorField = get(errors, field.name)
        isInvalid = touchedField && errorField
    }

    const getBoxShadow = (state) => {
        const shadowBase = '0 0 0 1px '
        if (isInvalid) return shadowBase + twColor.red['500']
        if (state.isFocused) return shadowBase + twColor[themeColor][primaryColorLevel]
        return 'none'
    }

    const styles = {
        control: (provided, state) => ({
            ...provided,
            height: '34px',
            minHeight: "34px",
            // ── Light gray bg, no border, rounded ──
            backgroundColor: '#F3F4F6',
            border: 'none !important',
            borderRadius: '10px',
            boxShadow: isInvalid
                ? '0 0 0 1px ' + twColor.red['500']
                : state.isFocused
                    ? '0 0 0 2px ' + twColor[themeColor][primaryColorLevel]
                    : 'none',
            '&:hover': {
                cursor: 'pointer',
                boxShadow: state.isFocused
                    ? '0 0 0 2px ' + twColor[themeColor][primaryColorLevel]
                    : 'none',
            },
            ...(isInvalid ? { borderColor: twColor.red['500'] } : {}),
        }),
        valueContainer: (provided) => ({
            ...provided,
            paddingLeft: '12px',
            paddingRight: '2px',
        }),
        singleValue: (provided) => ({
            ...provided,
            fontFamily: 'Inter, sans-serif',
            fontWeight: 600,
            fontSize: '13px',
            color: '#111827',
            margin: 0,
        }),
        input: (css) => ({
            ...css,
            fontFamily: 'Inter, sans-serif',
            fontWeight: 600,
            fontSize: '13px',
            color: '#111827',
            input: {
                outline: 'none',
                outlineOffset: 0,
                boxShadow: 'none !important',
            },
        }),
        menu: (provided) => ({
            ...provided,
            zIndex: 50,
            borderRadius: '10px',
            border: 'none',
            boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
            overflow: 'hidden',
        }),
        option: (provided, state) => ({
            ...provided,
            fontFamily: 'Inter, sans-serif',
            fontWeight: state.isSelected ? 600 : 400,
            fontSize: '13px',
            color: state.isSelected ? '#6366F1' : '#111827',
            backgroundColor: state.isSelected
                ? '#F3F4F6'
                : state.isFocused
                    ? '#F9FAFB'
                    : 'transparent',
            cursor: 'pointer',
        }),
        dropdownIndicator: (provided) => ({
            ...provided,
            padding: 0,
        }),
        indicatorsContainer: (provided) => ({
            ...provided,
            paddingRight: '0px',
        }),
        ...style,
    }

    const selectClass = classNames(
        'select',
        `select-${selectSize}`,
        className
    )

    return (
        <Component
            className={selectClass}
            classNamePrefix={'select'}
            ref={ref}
            styles={styles}
            components={{
                IndicatorSeparator: () => null,
                Option: DefaultOption,
                LoadingIndicator: DefaultLoadingIndicator,
                DropdownIndicator: DefaultDropdownIndicator,
                ClearIndicator: DefaultClearIndicator,
                ...components,
            }}
            {...field}
            {...rest}
        />
    )
})

Select.propTypes = {
    size: PropTypes.oneOf([SIZES.LG, SIZES.MD, SIZES.SM]),
    componentAs: PropTypes.elementType,
}

Select.defaultProps = {
    componentAs: ReactSelect,
}

export default Select